import {ControlledInput, ControlledToggle} from '@hm/fundamental'
import {
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormTrigger,
  useWatch
} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {
  type AdvancedFiltersFormValues,
  physicalFilterFields
} from './RefractoryAdvancedFilters.types'
import {RefractoryFilterResetButton} from './RefractoryFilterResetButton'

interface RefractoryPhysicalDataTabProps {
  control: Control<AdvancedFiltersFormValues>
  hidden: boolean
  setValue: UseFormSetValue<AdvancedFiltersFormValues>
  trigger: UseFormTrigger<AdvancedFiltersFormValues>
}

const units = {
  Metric: {bulkDensity: 'g/cm³', coldCrushingStrength: 'N/mm²', abrasionResistance: 'cm³'},
  US: {bulkDensity: 'lb/ft³', coldCrushingStrength: 'psi', abrasionResistance: 'in³'}
}

export const RefractoryPhysicalDataTab: React.FC<RefractoryPhysicalDataTabProps> = ({
  control,
  hidden,
  setValue,
  trigger
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const measurementSystem = useWatch({control, name: 'measurementSystem'})
  const ranges = useWatch({control, name: 'physicalData'})

  return (
    <div
      role="tabpanel"
      aria-label={t('refraCatalog.filters.physicalData')}
      hidden={hidden}
      data-test-id="refra_catalog_physical_filters"
    >
      <div className="-mx-2 mb-6 flex h-12 items-center gap-2">
        <span>{t('refraCatalog.filters.metric')}</span>
        <Controller
          name="measurementSystem"
          control={control}
          render={({field}) => (
            <ControlledToggle
              name={field.name}
              checked={field.value === 'US'}
              onChange={(event) => field.onChange(event.currentTarget.checked ? 'US' : 'Metric')}
              aria-label={t('refraCatalog.filters.us')}
              data-cy="refra-catalog-measurement-system"
            />
          )}
        />
        <span>{t('refraCatalog.filters.us')}</span>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_64px_64px_32px] items-start gap-3 sm:grid-cols-[minmax(0,1fr)_91px_91px_48px]">
        <span />
        <span className="text-center text-14px leading-[18px] text-foreground_faded">
          {t('refraCatalog.filters.min')}
        </span>
        <span className="text-center text-14px leading-[18px] text-foreground_faded">
          {t('refraCatalog.filters.max')}
        </span>
        <span />
        {physicalFilterFields.map(({name, bounds}) => {
          const label = t(`refraCatalog.filters.${name}`, {
            unit:
              name === 'porosity' || name === 'shelfLife'
                ? undefined
                : units[measurementSystem][name]
          })

          return (
            <div key={name} className="contents">
              <span className="flex min-h-12 min-w-0 items-center break-words text-16px">
                {label}
              </span>
              {(['min', 'max'] as const).map((bound) =>
                bounds.some((supportedBound) => supportedBound === bound) ? (
                  <Controller
                    key={bound}
                    name={`physicalData.${name}.${bound}`}
                    control={control}
                    render={({field, fieldState}) => (
                      <ControlledInput
                        {...field}
                        onChange={(event) => {
                          field.onChange(event)
                          void trigger(`physicalData.${name}`)
                        }}
                        type="number"
                        min={0}
                        max={name === 'porosity' ? 100 : undefined}
                        step="any"
                        placeholder="-"
                        aria-label={`${label} ${t(`refraCatalog.filters.${bound}`)}`}
                        aria-invalid={Boolean(fieldState.error)}
                        error={fieldState.error?.message}
                        isTouched
                        className="min-w-0"
                        data-cy={`refra-catalog-${name}-${bound}`}
                      />
                    )}
                  />
                ) : (
                  <span key={bound} />
                )
              )}
              <RefractoryFilterResetButton
                name={name}
                label={label}
                range={ranges[name]}
                onReset={() =>
                  setValue(
                    `physicalData.${name}`,
                    {min: '', max: ''},
                    {shouldDirty: true, shouldValidate: true}
                  )
                }
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
